"use client";

import NumberFlow from "@number-flow/react";
import { useRouter } from "next/navigation";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Icon, type IconName } from "@/components/ui/icon";
import {
  Table,
  TableBody,
  TableCell,
  TableFillerRow,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  TableEmptyOverlay,
  TableSkeletonBody,
  useTableSkeletonCount,
} from "@/components/ui/table";

import type {
  SequenceListAnalytics,
  SequenceListItem,
} from "../../hooks/use-sequences";
import { RowActionsMenu, SequenceActionsContextMenuContent, useSequenceRowActions } from "./row-actions-menu";
import { TableRowContextMenu } from "@/components/ui/table-row-context-menu";
import { useRef } from "react";

const COLUMN_COUNT = 10;

export interface KpiColumn {
  label: string;
  key: keyof SequenceListAnalytics;
  icon: IconName;
  iconColor: string;
}

/**
 * KPI columns for the sequence list. Icons + colours mirror the enrollment
 * list KPI cards (`enrollment-kpis.tsx`) so the two surfaces read the same.
 * "Replied" has no KPI card, so it borrows the reply-rate icon from the
 * overview sidebar.
 */
export const KPI_COLUMNS: readonly KpiColumn[] = [
  {
    label: "Total",
    key: "total",
    icon: "IconPaperPlane",
    iconColor: "text-[#f59e0b]",
  },
  {
    label: "Active",
    key: "in_progress",
    icon: "IconLoader",
    iconColor: "text-[#38bdf8]",
  },
  {
    label: "Finished",
    key: "finished",
    icon: "IconCircleCheck",
    iconColor: "text-[#4ade80]",
  },
  {
    label: "Opened",
    key: "opened",
    icon: "IconEmailNotification",
    iconColor: "text-[#818cf8]",
  },
  {
    label: "Clicked",
    key: "clicked",
    icon: "IconCursorClick",
    iconColor: "text-[#fb923c]",
  },
  {
    label: "Replied",
    key: "replied",
    icon: "IconArrowShareLeft",
    iconColor: "text-[#60a5fa]",
  },
  {
    label: "Interested",
    key: "interested",
    icon: "IconThumbsUp",
    iconColor: "text-[#f472b6]",
  },
  {
    label: "Bounced",
    key: "bounced",
    icon: "IconRedirectArrow",
    iconColor: "text-[#f87171]",
  },
] as const;

function senderInitials(name: string): string {
  const parts = name
    .split(/\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "?";
  return (
    (parts[0][0] ?? "") + (parts[parts.length - 1][0] ?? "")
  ).toUpperCase();
}

interface TableProps {
  sequences: SequenceListItem[];
  jobId: string;
  showPercentages?: boolean;
}

export function KpiColumnHeader({ col }: { col: KpiColumn }) {
  return (
    <span className="flex items-center justify-center gap-1">
      <Icon
        name={col.icon}
        size={14}
        className={`shrink-0 ${col.iconColor}`}
      />
      <span className="truncate">{col.label}</span>
    </span>
  );
}

function SequencesTHead({ sticky }: { sticky?: boolean }) {
  return (
    <TableHeader sticky={sticky}>
      <TableRow>
        <TableHead className="w-[24%] min-w-0">Name</TableHead>
        {KPI_COLUMNS.map((col) => (
          <TableHead key={col.key} className="w-[9%] text-center">
            <KpiColumnHeader col={col} />
          </TableHead>
        ))}
        <TableHead className="w-[4%]" aria-label="Actions" />
      </TableRow>
    </TableHeader>
  );
}

function skeletonCells() {
  return [
    <div key="name" className="flex flex-col gap-1.5">
      <div className="h-4 w-36 shimmer shimmer-bg text-foreground rounded bg-contrast/6" />
      <div className="h-3 w-24 shimmer shimmer-bg text-foreground rounded bg-contrast/4" />
    </div>,
    ...KPI_COLUMNS.map((col) => (
      <div key={col.key} className="flex flex-col items-center gap-0.5">
        <div className="h-4 w-6 shimmer shimmer-bg text-foreground rounded bg-contrast/6" />
      </div>
    )),
    <div key="actions" />,
  ];
}

export function SequenceTable({
  sequences,
  jobId,
  showPercentages = false,
}: TableProps) {
  const router = useRouter();
  const emptyRef = useRef<HTMLDivElement>(null);
  const emptySkeletonCount = useTableSkeletonCount(emptyRef);

  if (sequences.length === 0) {
    return (
      <TableEmptyOverlay
        containerRef={emptyRef}
        title="No sequences match the current filters"
        description="Try adjusting your filters or search query."
        backdrop={
          <Table variant="list" layout="fixed">
            <SequencesTHead />
            <TableSkeletonBody
              count={emptySkeletonCount}
              renderCells={skeletonCells}
            />
          </Table>
        }
      />
    );
  }

  return (
    <div data-tour="seq-table" className="flex flex-1 flex-col overflow-hidden">
      <div className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden bg-surface-primary">
        <Table variant="list" layout="fixed">
          <SequencesTHead sticky />
          <TableBody>
            {sequences.map((seq) => (
              <SequenceRow
                key={seq.id}
                seq={seq}
                jobId={jobId}
                showPercentages={showPercentages}
                onOpen={() => router.push(`/jobs/${jobId}/sequences/${seq.id}`)}
              />
            ))}
            <TableFillerRow columns={COLUMN_COUNT} />
          </TableBody>
        </Table>
      </div>

      <TablePagination
        page={1}
        total={sequences.length}
        perPage={25}
        onPageChange={() => {}}
        label={(t) => `${t} sequence${t !== 1 ? "s" : ""}`}
      />
    </div>
  );
}

interface RowProps {
  seq: SequenceListItem;
  jobId: string;
  showPercentages: boolean;
  onOpen: () => void;
}

function SequenceRow({ seq, jobId, showPercentages, onOpen }: RowProps) {
  const actions = useSequenceRowActions(seq, jobId);
  const senderName = seq.sender?.name ?? "Unknown";
  const stepCount = seq.step_count ?? 0;
  const stepLabel = `${stepCount} ${stepCount === 1 ? "Step" : "Steps"}`;
  const totalEnrollments = seq.analytics?.total ?? 0;

  return (
    <TableRowContextMenu
      content={
        <SequenceActionsContextMenuContent seq={seq} actions={actions} />
      }
    >
      <TableRow onClick={onOpen} className="group/row">
      <TableCell className="min-w-0">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="min-w-0 truncate text-sm text-contrast/80">
              {seq.name}
            </span>
            <span className="shrink-0 text-contrast/25">•</span>
            <span className="flex shrink-0 items-center gap-1 text-xs text-contrast/50">
              <Icon name="IconAgent" size={13} />
              {stepLabel}
            </span>
          </div>
          <div className="flex items-center gap-1.5 overflow-hidden">
            <MetaBadge>
              <Avatar className="size-[13px] shrink-0">
                <AvatarImage src={seq.sender?.avatar_url ?? undefined} alt="" />
                <AvatarFallback className="text-[8px] font-medium">
                  {senderInitials(senderName)}
                </AvatarFallback>
              </Avatar>
              <span className="truncate">{senderName}</span>
            </MetaBadge>
          </div>
        </div>
      </TableCell>

      {KPI_COLUMNS.map((col) => {
        const value = seq.analytics?.[col.key] ?? 0;
        const isTotal = col.key === "total";
        const isEmpty = totalEnrollments === 0;

        // Percentage mode applies to every column except Total (the
        // denominator). A zero or empty value still renders "0%" so the
        // toggle never leaves a dash behind. NumberFlow rolls the digits
        // between the two modes instead of snapping.
        const isPct = !isTotal && showPercentages;
        const pct =
          totalEnrollments === 0
            ? 0
            : Math.round((value / totalEnrollments) * 100);
        const shown = isPct ? pct : value;
        const muted = isPct ? pct === 0 : isEmpty;

        return (
          <TableCell key={col.key} className="text-center">
            {!isPct && isEmpty ? (
              <span className="text-sm text-muted-foreground">-</span>
            ) : (
              <NumberFlow
                value={shown}
                suffix={isPct ? "%" : undefined}
                className={
                  muted
                    ? "text-sm tabular-nums text-muted-foreground"
                    : "text-sm tabular-nums text-contrast/80"
                }
              />
            )}
          </TableCell>
        );
      })}

      <TableCell className="text-right">
        <RowActionsMenu seq={seq} actions={actions} />
      </TableCell>
      </TableRow>
    </TableRowContextMenu>
  );
}

function MetaBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex min-w-0 shrink items-center gap-0.5 overflow-hidden rounded-md border border-border bg-contrast/4 px-1 py-0.5 text-xs font-medium leading-4 text-contrast/75">
      {children}
    </span>
  );
}
